import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Building2,
  ChevronDown,
  Copy,
  FolderOpen,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  Permission,
  PROJECT_TRASH_DAYS,
  projectTrashExpiresAt,
  romaniaDateKey,
  ROMANIA_TIME_ZONE,
  UserRole,
  type ProjectDetails,
  type ProjectListItem,
  type ProjectsView,
  type ProjectStatusValue,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { EmptyState, ErrorState, CardSkeleton, TableRowSkeleton } from '@/components/feedback';
import { useAuth } from '@/features/auth/useAuth';
import {
  projectsQueryKeys,
  useDeleteProject,
  useProjects,
  usePurgeProject,
} from '@/features/projects/hooks';
import { projectsApi } from '@/features/projects/api';
import { errorKeyFor } from '@/features/projects/errorMessages';
import { describeError } from '@/lib/form/errorMessages';
import { useUsers } from '@/features/users/hooks';
import { toListQuery, type ProjectsListSearch } from '@/features/projects/search';
import { projectStatusBadgeClass, PROJECT_STATUS_VALUES } from '@/features/projects/statusBadge';
import { projectStatusLabel } from '@/features/projects/statusLabel';
import { clickableRowProps } from '@/lib/clickableRow';
import { RowCard } from '@/components/RowCard';
import { ConfirmDialog } from '@/features/users/components/ConfirmDialog';
import { ColumnOrderMenu } from '@/components/table/ColumnOrderMenu';
import { usePersistedColumnOrder } from '@/components/table/usePersistedColumnOrder';
import {
  ProjectCreateDialog,
  type ProjectCreateDialogInitialValues,
} from '@/features/projects/components/ProjectCreateDialog';
import { ProjectEditDialog } from '@/features/projects/components/ProjectEditDialog';
import { ProjectMergeDialog } from '@/features/projects/components/ProjectMergeDialog';
import { MergeProjectsIcon } from '@/features/projects/components/MergeProjectsIcon';

type FilterKey = 'agent' | 'status' | 'year' | 'month';
type FilterOption = { value: string; label: string };
interface ProjectsTableColumn {
  key: string;
  label: string;
  className?: string;
  render: (project: ProjectListItem) => ReactNode;
}

/** One client's board: the client, and every project of theirs on this page. */
interface ClientGroup {
  clientId: string;
  clientName: string;
  projects: ProjectListItem[];
}

const MONTH_VALUES = Array.from({ length: 12 }, (_, index) => index + 1);

const VIEW_TABS: ProjectsView[] = ['ACTIVE', 'ARCHIVED', 'DELETED'];

export function ProjectsListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as ProjectsListSearch;

  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canCreate = can(Permission.PROJECTS_CREATE);
  const canUpdate = can(Permission.PROJECTS_UPDATE);
  const canDelete = can(Permission.PROJECTS_DELETE);
  // A merge creates one project and deletes the ones that went into it.
  const canMerge = canCreate && canDelete;

  const view: ProjectsView = search.view ?? 'ACTIVE';
  const isAdmin = user?.role === UserRole.ADMIN;
  const query = useMemo(() => toListQuery(search), [search]);
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useProjects(query);

  const usersQuery = useUsers({
    page: 1,
    pageSize: 100,
    active: true,
    includeDeleted: false,
  });
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of usersQuery.data?.items ?? []) m.set(u.id, u.fullName);
    return m;
  }, [usersQuery.data]);

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneInitialValues, setCloneInitialValues] = useState<
    ProjectCreateDialogInitialValues | undefined
  >(undefined);
  // The project the clone dialog copies from. Set together with the pre-filled
  // values, and what turns the dialog from "create" into "copy".
  const [cloneSourceId, setCloneSourceId] = useState<string | undefined>(undefined);
  const [cloneLoadingId, setCloneLoadingId] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState(false);
  const [editProject, setEditProject] = useState<ProjectDetails | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [editError, setEditError] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<ProjectListItem | null>(null);
  const [mergeSeed, setMergeSeed] = useState<ProjectListItem | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const deleteMut = useDeleteProject(deleteTarget?.id ?? '');
  const purgeMut = usePurgeProject(purgeTarget?.id ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cloneRequestRef = useRef(0);
  const editRequestRef = useRef(0);
  useEffect(() => setSearchInput(search.search ?? ''), [search.search]);

  const items = data?.items ?? [];
  const clientGroups = useMemo(() => groupByClient(items), [items]);
  const currentPage = search.page ?? 1;
  const pageSize = search.pageSize ?? 25;
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const agentOptions = useMemo<FilterOption[]>(
    () => (usersQuery.data?.items ?? []).map((u) => ({ value: u.id, label: u.fullName })),
    [usersQuery.data],
  );
  const statusOptions = useMemo<FilterOption[]>(
    () =>
      PROJECT_STATUS_VALUES.map((status) => ({
        value: status,
        label: t(`projects.status.${status}`),
      })),
    [t],
  );
  const yearOptions = useMemo<FilterOption[]>(() => {
    // Years as Romania reads them: a deadline is a date at UTC midnight, which
    // is that same date there, and a creation instant is dated in Romania.
    const yearOf = (ms: number) => Number(romaniaDateKey(ms).slice(0, 4));
    const currentYear = yearOf(Date.now());
    const years = new Set<number>();
    for (let y = currentYear - 4; y <= currentYear + 2; y += 1) years.add(y);
    for (const project of items) {
      years.add(yearOf(Date.parse(project.deadline ?? project.createdAt)));
    }
    return [...years]
      .sort((a, b) => b - a)
      .map((year) => ({ value: String(year), label: String(year) }));
  }, [items]);
  const monthOptions = useMemo<FilterOption[]>(
    () =>
      MONTH_VALUES.map((month) => ({
        value: String(month),
        label: t(`projects.months.${month}`),
      })),
    [t],
  );
  const cloneTooltipLabel = t('projects.actions.cloneProject') ?? 'Clone project';
  const editTooltipLabel = t('projects.actions.editProject') ?? 'Edit project';

  const updateSearch = (patch: Partial<ProjectsListSearch>, opts: { replace?: boolean } = {}) => {
    const next: ProjectsListSearch = { ...search, ...patch };
    const clean: ProjectsListSearch = {};
    if (next.view && next.view !== 'ACTIVE') clean.view = next.view;
    if (next.page && next.page !== 1) clean.page = next.page;
    if (next.pageSize && next.pageSize !== 25) clean.pageSize = next.pageSize;
    if (next.search) clean.search = next.search;
    if (next.status?.length) clean.status = next.status;
    if (next.clientId) clean.clientId = next.clientId;
    if (next.agentId?.length) clean.agentId = next.agentId;
    if (next.year?.length) clean.year = next.year;
    if (next.month?.length) clean.month = next.month;
    if (next.assignedToMe) clean.assignedToMe = true;
    if (next.includeDeleted) clean.includeDeleted = true;
    if (next.upcomingDeadline) clean.upcomingDeadline = true;
    void navigate({ to: '/projects', search: clean, replace: opts.replace });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      updateSearch({ search: trimmed || undefined, page: 1 }, { replace: true });
    }, 300);
  };

  const resetFilters = () => {
    setSearchInput('');
    setOpenFilter(null);
    // Clearing the filters must not also throw you out of "Arhivă" — the
    // section is where you are, not something you filtered by.
    void navigate({
      to: '/projects',
      search: view === 'ACTIVE' ? {} : { view },
      replace: true,
    });
  };

  const changeView = (next: ProjectsView) => {
    setSearchInput('');
    setOpenFilter(null);
    setActionError(null);
    void navigate({ to: '/projects', search: next === 'ACTIVE' ? {} : { view: next } });
  };

  /** Archive / unarchive / restore: one row, one request, one shared error line. */
  const runRowAction = async (
    project: ProjectListItem,
    action: 'ARCHIVE' | 'UNARCHIVE' | 'RESTORE',
  ) => {
    setActionError(null);
    setActionId(project.id);
    try {
      if (action === 'ARCHIVE') await projectsApi.archive(project.id);
      else if (action === 'UNARCHIVE') await projectsApi.unarchive(project.id);
      else await projectsApi.restore(project.id);
      await queryClient.invalidateQueries({ queryKey: projectsQueryKeys.all });
    } catch (err) {
      setActionError(err);
    } finally {
      setActionId(null);
    }
  };

  const confirmPurge = async () => {
    if (!purgeTarget) return;
    try {
      await purgeMut.mutateAsync();
      setPurgeTarget(null);
      purgeMut.reset();
    } catch {
      /* surfaced below */
    }
  };

  const trashDaysLeft = (deletedAt: string | null) => {
    if (!deletedAt) return 0;
    const ms = projectTrashExpiresAt(deletedAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
  };

  const toggleStringFilter = (key: 'agentId', value: string) => {
    const current = search[key] ?? [];
    const next = toggleValue(current, value);
    updateSearch({ [key]: next.length ? next : undefined, page: 1 });
  };
  const toggleStatusFilter = (value: string) => {
    const current = search.status ?? [];
    const next = toggleValue(current, value as ProjectStatusValue);
    updateSearch({ status: next.length ? next : undefined, page: 1 });
  };
  const toggleNumberFilter = (key: 'year' | 'month', value: string) => {
    const parsed = Number(value);
    const current = search[key] ?? [];
    const next = toggleValue(current, parsed);
    updateSearch({ [key]: next.length ? next : undefined, page: 1 });
  };

  const formatDeadline = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat('ro-RO', { timeZone: ROMANIA_TIME_ZONE }).format(
        new Date(iso),
      );
    } catch {
      return '—';
    }
  };

  const roomCountLabel = (count: number) =>
    t('projects.columns.roomCountValue', { count, defaultValue: `${count} cameră(e)` });

  const openCreateDialog = () => {
    cloneRequestRef.current += 1;
    setCloneInitialValues(undefined);
    setCloneSourceId(undefined);
    setCloneLoadingId(null);
    setCloneError(false);
    setCreateOpen(true);
  };

  const closeCreateDialog = () => {
    cloneRequestRef.current += 1;
    setCreateOpen(false);
    setCloneInitialValues(undefined);
    setCloneSourceId(undefined);
    setCloneLoadingId(null);
  };

  const cloneProject = async (project: ProjectListItem) => {
    const requestId = cloneRequestRef.current + 1;
    cloneRequestRef.current = requestId;
    setCloneError(false);
    setCloneLoadingId(project.id);
    try {
      const details = await queryClient.fetchQuery({
        queryKey: projectsQueryKeys.detail(project.id),
        queryFn: () => projectsApi.get(project.id),
      });
      if (cloneRequestRef.current !== requestId) return;
      setCloneInitialValues({
        clientId: details.clientId,
        title: details.title,
        agentId: details.agentId ?? '',
        // Not `details.status`: a copy always starts in IN_PROGRESS, and the
        // dialog hides the picker in copy mode rather than show a dead field.
        status: 'IN_PROGRESS',
        // Blank, not inherited: the source's deadline is a date that job was
        // promised for — it feeds the upcoming-deadline filter and the
        // scheduler — and its notes describe that job. A re-quote sets its own.
        deadline: '',
        notes: '',
        // A clone of a manually priced job starts from the same figures; blank
        // for a caller who cannot see financials, since they never arrive.
        manualMaterialCost: details.financials?.manualMaterialCost ?? '',
        manualOfferedPrice: details.financials?.manualOfferedPrice ?? '',
      });
      setCloneSourceId(project.id);
      setCreateOpen(true);
    } catch {
      if (cloneRequestRef.current === requestId) setCloneError(true);
    } finally {
      if (cloneRequestRef.current === requestId) setCloneLoadingId(null);
    }
  };

  const editProjectData = async (project: ProjectListItem) => {
    const requestId = editRequestRef.current + 1;
    editRequestRef.current = requestId;
    setEditError(false);
    setEditLoadingId(project.id);
    try {
      const details = await queryClient.fetchQuery({
        queryKey: projectsQueryKeys.detail(project.id),
        queryFn: () => projectsApi.get(project.id),
      });
      if (editRequestRef.current !== requestId) return;
      setEditProject(details);
    } catch {
      if (editRequestRef.current === requestId) setEditError(true);
    } finally {
      if (editRequestRef.current === requestId) setEditLoadingId(null);
    }
  };

  const closeEditDialog = () => {
    editRequestRef.current += 1;
    setEditProject(null);
    setEditLoadingId(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync();
      setDeleteTarget(null);
      deleteMut.reset();
    } catch {
      /* error surfaced below */
    }
  };

  const tableColumns: ProjectsTableColumn[] = [
    {
      key: 'title',
      label: t('projects.columns.title'),
      render: (p) => (
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-primary" />
          <span className="font-medium">{p.title}</span>
          {p.deletedAt && (
            <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
              {view === 'DELETED'
                ? t('projects.badges.trashDaysLeft', { count: trashDaysLeft(p.deletedAt) })
                : t('projects.badges.deleted')}
            </span>
          )}
          {view === 'ARCHIVED' && p.archivedAt && (
            <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {t('projects.badges.archivedOn', { date: formatDeadline(p.archivedAt) })}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'client',
      label: t('projects.columns.client'),
      className: 'text-muted-foreground',
      render: (p) => <span className="font-bold italic">{p.clientName}</span>,
    },
    {
      key: 'roomCount',
      label: t('projects.columns.roomCount'),
      className: 'text-muted-foreground',
      render: (p) => roomCountLabel(p.roomCount ?? 0),
    },
    {
      key: 'createdBy',
      label: t('projects.columns.createdBy'),
      className: 'text-muted-foreground',
      render: (p) => p.createdByFullName,
    },
    {
      key: 'agent',
      label: t('projects.columns.agent'),
      className: 'text-muted-foreground',
      render: (p) => (p.agentId ? (userNameById.get(p.agentId) ?? '—') : '—'),
    },
    {
      key: 'status',
      label: t('projects.columns.status'),
      render: (p) => (
        <span
          className={
            'inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-medium ' +
            projectStatusBadgeClass(p.status)
          }
        >
          {projectStatusLabel(t, p.status, p.reofferCount)}
        </span>
      ),
    },
    {
      key: 'deadline',
      label: t('projects.columns.deadline'),
      className: 'text-muted-foreground',
      render: (p) => formatDeadline(p.deadline),
    },
    {
      key: 'actions',
      label: t('projects.columns.actions'),
      className: 'text-right',
      render: (p) => (
        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          {rowActions(p)}
        </div>
      ),
    },
  ];

  /**
   * What a row offers depends on the section it is in, because the three mean
   * different things: the everyday list edits, the archive only lets a project
   * out, and the trash restores or destroys.
   */
  function rowActions(p: ProjectListItem) {
    if (view === 'DELETED') {
      return (
        <>
          <ActionTooltip label={t('projects.actions.restore')}>
            <Button
              type="button"
              onClick={() => void runRowAction(p, 'RESTORE')}
              disabled={actionId === p.id}
              className="h-9 w-9 rounded-full p-0"
              aria-label={t('projects.actions.restore')}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </ActionTooltip>
          <ActionTooltip label={t('projects.actions.purge')}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPurgeTarget(p)}
              className="h-9 w-9 rounded-full bg-destructive/15 p-0 text-destructive hover:bg-destructive/25"
              aria-label={t('projects.actions.purge')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </ActionTooltip>
        </>
      );
    }
    if (view === 'ARCHIVED') {
      // The one action an archived project has: everything else is refused by
      // the API anyway, so offering it here would only produce an error.
      return canUpdate ? (
        <ActionTooltip label={t('projects.actions.unarchive')}>
          <Button
            type="button"
            onClick={() => void runRowAction(p, 'UNARCHIVE')}
            disabled={actionId === p.id}
            className="h-9 w-9 rounded-full p-0"
            aria-label={t('projects.actions.unarchive')}
          >
            <ArchiveRestore className="h-4 w-4" />
          </Button>
        </ActionTooltip>
      ) : null;
    }
    return (
      <>
        {canUpdate && p.status === 'IN_WARRANTY' && (
          <ActionTooltip label={t('projects.actions.archive')}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void runRowAction(p, 'ARCHIVE')}
              disabled={actionId === p.id}
              className="h-9 w-9 rounded-full bg-muted p-0 hover:bg-muted/80"
              aria-label={t('projects.actions.archive')}
            >
              <Archive className="h-4 w-4" />
            </Button>
          </ActionTooltip>
        )}
        {canDelete && !p.deletedAt && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDeleteTarget(p)}
            className="h-9 w-9 rounded-full bg-destructive/15 p-0 text-destructive hover:bg-destructive/25"
            aria-label={t('projects.danger.deleteButton') ?? 'delete'}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        {canMerge && !p.deletedAt && (
          <ActionTooltip
            label={p.inScheduler ? t('projects.merge.inScheduler') : t('projects.merge.action')}
          >
            <Button
              type="button"
              variant="ghost"
              onClick={() => setMergeSeed(p)}
              disabled={p.inScheduler}
              className="h-9 w-9 rounded-full bg-muted p-0 hover:bg-muted/80"
              aria-label={t('projects.merge.action')}
            >
              <MergeProjectsIcon className="h-4 w-4" />
            </Button>
          </ActionTooltip>
        )}
        {canCreate && (
          <ActionTooltip label={cloneTooltipLabel}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void cloneProject(p)}
              disabled={cloneLoadingId === p.id}
              className="h-9 w-9 rounded-full bg-muted p-0 hover:bg-muted/80"
              aria-label={cloneTooltipLabel}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </ActionTooltip>
        )}
        {canUpdate && (
          <ActionTooltip label={editTooltipLabel}>
            <Button
              type="button"
              onClick={() => void editProjectData(p)}
              disabled={editLoadingId === p.id}
              className="h-9 w-9 rounded-full p-0"
              aria-label={editTooltipLabel}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </ActionTooltip>
        )}
      </>
    );
  }

  const { orderedColumns, moveColumn, resetColumnOrder } = usePersistedColumnOrder(
    'projects.list',
    tableColumns,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold">{t('projects.title')}</h1>
      </div>

      <div className="flex flex-wrap gap-1 rounded-2xl border border-border bg-card p-1">
        {VIEW_TABS.filter((tab) => tab !== 'DELETED' || isAdmin).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => changeView(tab)}
            aria-current={view === tab ? 'page' : undefined}
            className={
              'rounded-xl px-4 py-2 text-sm font-medium transition-colors ' +
              (view === tab
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted')
            }
          >
            {t(`projects.views.${tab}`)}
          </button>
        ))}
      </div>

      {view === 'ARCHIVED' && (
        <p className="rounded-2xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t('projects.views.archivedHint')}
        </p>
      )}
      {view === 'DELETED' && (
        <p className="rounded-2xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t('projects.views.deletedHint', { days: PROJECT_TRASH_DAYS })}
        </p>
      )}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('projects.filters.searchPlaceholder') ?? ''}
            className="h-12 w-full rounded-2xl border border-border bg-background pl-12 pr-4 text-base focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {canCreate && view === 'ACTIVE' && (
          <Button
            type="button"
            onClick={openCreateDialog}
            className="h-12 w-full rounded-2xl px-6 text-base lg:w-auto"
          >
            <Plus className="mr-2 h-5 w-5" />
            {t('projects.new')}
          </Button>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_auto_auto] xl:items-end">
          <MultiSelectFilter
            label={t('projects.filters.agent')}
            allLabel={t('projects.filters.allAgents')}
            selectedCountLabel={(count) => t('projects.filters.selectedCount', { count })}
            options={agentOptions}
            values={search.agentId ?? []}
            open={openFilter === 'agent'}
            onOpenChange={(open) => setOpenFilter(open ? 'agent' : null)}
            onToggle={(value) => toggleStringFilter('agentId', value)}
          />
          <MultiSelectFilter
            label={t('projects.filters.status')}
            allLabel={t('projects.filters.allStatuses')}
            selectedCountLabel={(count) => t('projects.filters.selectedCount', { count })}
            options={statusOptions}
            values={search.status ?? []}
            open={openFilter === 'status'}
            onOpenChange={(open) => setOpenFilter(open ? 'status' : null)}
            onToggle={toggleStatusFilter}
          />
          <MultiSelectFilter
            label={t('projects.filters.year')}
            allLabel={t('projects.filters.allYears')}
            selectedCountLabel={(count) => t('projects.filters.selectedCount', { count })}
            options={yearOptions}
            values={(search.year ?? []).map(String)}
            open={openFilter === 'year'}
            onOpenChange={(open) => setOpenFilter(open ? 'year' : null)}
            onToggle={(value) => toggleNumberFilter('year', value)}
          />
          <MultiSelectFilter
            label={t('projects.filters.month')}
            allLabel={t('projects.filters.allMonths')}
            selectedCountLabel={(count) => t('projects.filters.selectedCount', { count })}
            options={monthOptions}
            values={(search.month ?? []).map(String)}
            open={openFilter === 'month'}
            onOpenChange={(open) => setOpenFilter(open ? 'month' : null)}
            onToggle={(value) => toggleNumberFilter('month', value)}
          />
          <button
            type="button"
            onClick={() =>
              updateSearch({
                upcomingDeadline: search.upcomingDeadline ? undefined : true,
                page: 1,
              })
            }
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-border px-4 text-sm font-medium text-foreground hover:bg-muted/50"
          >
            <span
              className={
                'h-4 w-4 rounded-sm border border-muted-foreground ' +
                (search.upcomingDeadline ? 'bg-foreground' : 'bg-transparent')
              }
            />
            {t('projects.filters.upcomingDeadline')}
          </button>
          <Button
            type="button"
            variant="ghost"
            onClick={resetFilters}
            className="h-12 rounded-2xl bg-muted px-5 text-base hover:bg-muted/80"
          >
            <X className="mr-2 h-4 w-4" />
            {t('projects.filters.reset')}
          </Button>
        </div>
      </div>

      {isError && <ErrorState error={error} onRetry={() => void refetch()} />}
      {deleteMut.isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {describeError(t, deleteMut.error, errorKeyFor)}
        </div>
      )}
      {cloneError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {t('projects.errors.cloneLoad')}
        </div>
      )}
      {editError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {t('projects.errors.editLoad')}
        </div>
      )}
      {(actionError !== null || purgeMut.isError) && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {describeError(t, actionError ?? purgeMut.error, errorKeyFor)}
        </div>
      )}

      <div
        className={
          'hidden md:block overflow-hidden rounded-2xl bg-card ' +
          (isPlaceholderData ? 'opacity-70 transition-opacity' : '')
        }
      >
        <div className="mb-2 flex justify-end">
          <ColumnOrderMenu
            columns={orderedColumns}
            onMoveColumn={moveColumn}
            onReset={resetColumnOrder}
          />
        </div>
        <Table>
          <THead className="bg-muted/40 text-sm normal-case tracking-normal text-foreground">
            <TR>
              {orderedColumns.map((column) => (
                <TH key={column.key} className={column.className}>
                  {column.label}
                </TH>
              ))}
            </TR>
          </THead>
          {isLoading ? <TableRowSkeleton columns={orderedColumns.length} /> : null}
          {!isLoading && data && items.length === 0 && (
            <TBody>
              <TR>
                <TD colSpan={orderedColumns.length} className="py-6">
                  <EmptyState description={t('projects.empty')} />
                </TD>
              </TR>
            </TBody>
          )}
          {/* One <tbody> per client, which is what makes each board a single
              row group for assistive tech rather than a styled divider. */}
          {clientGroups.map((group) => (
            <TBody
              key={group.clientId}
              className="border-t-8 border-t-background divide-y divide-border"
            >
              <TR className="bg-muted/60 hover:bg-muted/60">
                <TD
                  colSpan={orderedColumns.length}
                  className="border-l-4 border-l-primary/60 px-4 py-2.5"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Building2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span className="font-bold italic text-foreground">{group.clientName}</span>
                    <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {t('projects.groups.projectCount', { count: group.projects.length })}
                    </span>
                  </div>
                </TD>
              </TR>
              {group.projects.map((p, index) => (
                <TR
                  key={p.id}
                  {...clickableRowProps(() =>
                    navigate({ to: '/projects/$id', params: { id: p.id } }),
                  )}
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                >
                  {orderedColumns.map((column, columnIndex) => (
                    <TD
                      key={column.key}
                      className={
                        (columnIndex === 0 ? 'border-l-4 border-l-primary/60 ' : '') +
                        (index === group.projects.length - 1 ? 'pb-3 ' : '') +
                        (column.className ?? '')
                      }
                    >
                      {column.render(p)}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          ))}
        </Table>
      </div>

      <div
        className={
          'md:hidden space-y-2 ' + (isPlaceholderData ? 'opacity-70 transition-opacity' : '')
        }
      >
        {isLoading ? (
          <CardSkeleton count={4} className="grid-cols-1" />
        ) : data && items.length === 0 ? (
          <div className="rounded-lg border border-border bg-card py-6">
            <EmptyState description={t('projects.empty')} />
          </div>
        ) : (
          clientGroups.map((group) => (
            <section
              key={group.clientId}
              aria-label={group.clientName}
              className="space-y-2 rounded-xl border border-border bg-card p-2"
            >
              <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                <Building2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="font-bold italic text-foreground">{group.clientName}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {t('projects.groups.projectCount', { count: group.projects.length })}
                </span>
              </div>
              {group.projects.map((p) => (
                <RowCard
                  key={p.id}
                  onActivate={() => navigate({ to: '/projects/$id', params: { id: p.id } })}
                  title={
                    <span className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-primary" aria-hidden="true" />
                      {p.title}
                    </span>
                  }
                  badges={
                    <>
                      <span
                        className={
                          'inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-medium ' +
                          projectStatusBadgeClass(p.status)
                        }
                      >
                        {projectStatusLabel(t, p.status, p.reofferCount)}
                      </span>
                      {p.deletedAt && (
                        <span className="rounded-md bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                          {t('projects.badges.deleted')}
                        </span>
                      )}
                    </>
                  }
                  subtitle={<span className="font-bold italic">{p.clientName}</span>}
                  meta={
                    <>
                      <div>{roomCountLabel(p.roomCount ?? 0)}</div>
                      <div>{p.createdByFullName}</div>
                      <div>{p.agentId ? (userNameById.get(p.agentId) ?? '—') : '—'}</div>
                      <div>{formatDeadline(p.deadline)}</div>
                    </>
                  }
                  // Same source as the table's action cell, so a section can
                  // never offer on a phone what it withholds on a desktop.
                  actions={rowActions(p)}
                />
              ))}
            </section>
          ))
        )}
      </div>

      <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <div>
          {t('projects.pagination.range', {
            from: total === 0 ? 0 : (currentPage - 1) * pageSize + 1,
            to: Math.min(currentPage * pageSize, total),
            total,
          })}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={currentPage <= 1}
            onClick={() => updateSearch({ page: currentPage - 1 })}
          >
            {t('projects.pagination.prev')}
          </Button>
          <Button
            variant="outline"
            disabled={currentPage >= lastPage}
            onClick={() => updateSearch({ page: currentPage + 1 })}
          >
            {t('projects.pagination.next')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('projects.danger.confirmTitle')}
        description={t('projects.danger.confirmDescription', {
          code: deleteTarget?.code ?? '',
        })}
        confirmLabel={t('projects.danger.deleteButton')}
        variant="destructive"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
      <ConfirmDialog
        open={purgeTarget !== null}
        title={t('projects.danger.purgeTitle')}
        description={t('projects.danger.purgeDescription', { code: purgeTarget?.code ?? '' })}
        confirmLabel={t('projects.danger.purgeButton')}
        variant="destructive"
        loading={purgeMut.isPending}
        onCancel={() => setPurgeTarget(null)}
        onConfirm={confirmPurge}
      />
      {canCreate && (
        <ProjectCreateDialog
          open={createOpen}
          onClose={closeCreateDialog}
          initialValues={cloneInitialValues}
          duplicateFromId={cloneSourceId}
          title={cloneSourceId ? t('projects.create.cloneTitle') : undefined}
        />
      )}
      {canMerge && <ProjectMergeDialog seed={mergeSeed} onClose={() => setMergeSeed(null)} />}
      {canUpdate && (
        <ProjectEditDialog
          open={editProject !== null}
          project={editProject}
          onClose={closeEditDialog}
        />
      )}
    </div>
  );
}

function ActionTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute right-full top-1/2 z-50 mr-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background opacity-0 shadow-md transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {label}
        <span className="absolute left-full top-1/2 -translate-y-1/2 border-y-4 border-l-4 border-y-transparent border-l-foreground" />
      </span>
    </span>
  );
}

function MultiSelectFilter({
  label,
  allLabel,
  selectedCountLabel,
  options,
  values,
  open,
  onOpenChange,
  onToggle,
}: {
  label: string;
  allLabel: string;
  selectedCountLabel: (count: number) => string;
  options: FilterOption[];
  values: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggle: (value: string) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.filter((option) => values.includes(option.value));
  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? selected[0]!.label
        : selectedCountLabel(selected.length);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !rootRef.current?.contains(target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onOpenChange, open]);

  return (
    <div ref={rootRef} className="relative">
      <div className="mb-2 text-sm font-semibold text-foreground">{label}</div>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex h-12 w-full items-center justify-between rounded-2xl border border-border bg-background px-4 text-left text-base"
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 max-h-72 w-full overflow-y-auto rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-lg">
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{allLabel}</div>
          ) : (
            options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={values.includes(option.value)}
                  onChange={() => onToggle(option.value)}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                <span className="truncate">{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The page's projects as one board per client, in the order the API returned
 * them — so the list keeps its newest-first ordering and a client's board sits
 * where their most recent project would have been.
 *
 * Grouping is per page by design: the API still paginates by project, so a
 * client with projects spread far apart in time gets a board on each page they
 * appear on rather than one board pulling rows across page boundaries.
 */
function groupByClient(items: readonly ProjectListItem[]): ClientGroup[] {
  const groups = new Map<string, ClientGroup>();
  for (const project of items) {
    const group = groups.get(project.clientId);
    if (group) group.projects.push(project);
    else
      groups.set(project.clientId, {
        clientId: project.clientId,
        clientName: project.clientName,
        projects: [project],
      });
  }
  return [...groups.values()];
}

function toggleValue<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}
